<template>
  <rs-side-panel ref="sidePanel">
    <template slot="header">Müşteriye TYA Ekle</template>
    <template slot="body">
      <v-form @submit.prevent="handleFormSubmit" ref="form">
        <v-row cols="12" dense>
          <v-col sm="12">
            <rs-select-cluster
              label="TYA Seçin"
              v-model="formData.cluster_id"
              :rules="[rules.required]"
              :filled="isLoading || id"
              :readonly="isLoading || id"
              :admin="hasRole('superadministrator')"
              :clearable="!id"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-datepicker
              label="Başlangıç Tarihi"
              v-model="formData.starts_on"
              :rules="[rules.required]"
              hint="Ücretlendirme başlangıç tarihini belirler"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="6">
            <rs-datepicker
              label="Bitiş Tarihi"
              v-model="formData.ends_on"
              :disabled="!formData.starts_on"
              :rules="
                !formData.starts_on || !formData.ends_on
                  ? []
                  : [rules.min(formData.ends_on, formData.starts_on)]
              "
              :min="formData.starts_on"
              hint="Boş bırakılırsa ilişki açık uçlu kalır. İleri tarihli başka bir ilişki varsa bitiş tarihi girilmelidir."
              persistent-hint
            />
          </v-col>

          <v-col cols="12" v-if="transitionPreview.message" class="pt-4">
            <v-alert
              dense
              outlined
              :type="transitionPreview.canSave ? 'warning' : 'error'"
              class="mb-0 mt-1"
            >
              {{ transitionPreview.message }}
            </v-alert>
          </v-col>
        </v-row>

        <rs-form-buttons
          @cancel="handleCancelClick"
          :is-loading="isLoading"
          hide-submit
          @submit="handleFormSubmit"
        />
      </v-form>
    </template>
  </rs-side-panel>
</template>

<script>
import { hasForm, hasPermissions } from "@/view/mixins";

export default {
  mixins: [hasForm, hasPermissions],
  data() {
    return {
      id: null,
      transitionPreview: {
        canSave: true,
        message: null,
      },
      lastTransitionPreviewKey: null,
      formData: {
        customer_id: null,
        cluster_id: null,
        starts_on: new Date().toJSON().split("T")[0],
        ends_on: null,
      },
    };
  },
  watch: {
    "formData.cluster_id"() {
      this.loadTransitionWarning();
    },
    "formData.starts_on"() {
      this.loadTransitionWarning();
    },
    "formData.ends_on"() {
      this.loadTransitionWarning();
    },
  },
  methods: {
    resetTransitionPreview() {
      this.transitionPreview = {
        canSave: true,
        message: null,
      };
      this.lastTransitionPreviewKey = null;
    },
    isCompleteIsoDate(value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(value || "");
    },
    getTransitionPreviewKey() {
      return JSON.stringify({
        customerClusterId: this.id,
        clusterId: this.formData.cluster_id,
        startsOn: this.formData.starts_on,
        endsOn: this.formData.ends_on || null,
      });
    },
    show(customerId, id) {
      this.id = null;
      this.resetTransitionPreview();
      this.formData = {
        cluster_id: null,
        customer_id: null,
        starts_on: new Date().toJSON().split("T")[0],
        ends_on: null,
      };
      if (customerId) {
        this.formData.customer_id = customerId;
      } else {
        return false;
      }

      if (id) {
        this.id = id;
        this.load();
      }

      this.$nextTick(() => {
        this.$refs.sidePanel.show();
      });
    },
    load() {
      this.isLoading = true;

      return this.$api
        .query(`api-super-admin/customer-clusters/${this.id}`)
        .then((response) => {
          this.loadData(response);
          this.loadTransitionWarning();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    canSubmitForm() {
      if (!this.formData.customer_id) {
        return false;
      }

      if (!this.formData.cluster_id) {
        return false;
      }

      if (!this.isCompleteIsoDate(this.formData.starts_on)) {
        return false;
      }

      if (
        this.formData.ends_on &&
        !this.isCompleteIsoDate(this.formData.ends_on)
      ) {
        return false;
      }

      if (
        this.formData.ends_on &&
        this.formData.ends_on < this.formData.starts_on
      ) {
        return false;
      }

      return true;
    },
    async handleFormSubmit() {
      this.$refs.form.validate();

      if (this.isLoading || !this.canSubmitForm()) {
        this.$toast.error("Lütfen tüm gerekli alanları doldurunuz");
        return false;
      }

      const preview = await this.loadTransitionWarning();

      if (preview.canSave === false) {
        this.$toast.error(
          preview.message || "Bu müşteri ilişkisi bu tarihlerle kaydedilemez."
        );
        return false;
      }

      this.isLoading = true;
      const formData = Object.assign({}, this.formData);

      if (this.id) {
        this.$api
          .put(`api-super-admin/customer-clusters/${this.id}`, formData)
          .then(() => {
            this.$toast.success("Kaydedildi");
            this.$emit("saved");
            this.hide();
          })
          .catch((error) => {
            this.handleError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$api
          .post(`api-super-admin/customer-clusters`, formData)
          .then(() => {
            this.$toast.success("Kaydedildi");
            this.$emit("saved");
            this.hide();
          })
          .catch((error) => {
            this.handleError(error);
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    handleCancelClick() {
      this.hide();
    },
    hide() {
      this.$refs.sidePanel.hide();
    },
    loadTransitionWarning() {
      if (
        !this.formData.cluster_id ||
        !this.isCompleteIsoDate(this.formData.starts_on) ||
        (this.formData.ends_on &&
          !this.isCompleteIsoDate(this.formData.ends_on))
      ) {
        this.resetTransitionPreview();
        return Promise.resolve(this.transitionPreview);
      }

      const previewKey = this.getTransitionPreviewKey();

      if (previewKey === this.lastTransitionPreviewKey) {
        return Promise.resolve(this.transitionPreview);
      }

      this.lastTransitionPreviewKey = previewKey;

      return this.$api
        .query("api-super-admin/customer-clusters/preview-transition", {
          params: {
            cluster_id: this.formData.cluster_id,
            starts_on: this.formData.starts_on,
            ends_on: this.formData.ends_on,
            customer_cluster_id: this.id,
          },
        })
        .then((response) => {
          const data = response?.data?.data || {};

          this.transitionPreview = {
            canSave: data.can_save !== false,
            message: data.message || null,
          };

          return this.transitionPreview;
        })
        .catch((error) => {
          this.resetTransitionPreview();

          if (error?.response?.status !== 422) {
            this.handleError(error);
          }

          return this.transitionPreview;
        });
    },
  },
};
</script>
